import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axiosConfig from "../../../api/axiosConfig";
import SearchableSelect from './components/SearchableSelect';
import FilterStatisticsCard from './components/FilterStatisticsCard';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function PastLessonsPage() {
  // Loading states
  const [isLoadingLessons, setIsLoadingLessons] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  
  // Data states
  const [lessons, setLessons] = useState([]);
  const [kursSatisList, setKursSatisList] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage] = useState(50);
  
  // Form states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentLesson, setCurrentLesson] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    kurs_satis_id: '',
    ogretmen_id: '',
    ders_id: '',
    ders_konusu: '',
    ders_sure: '40',
    baslangic_tarihi: ''
  });
  
  // Selected kurs satis details
  const [selectedKursSatis, setSelectedKursSatis] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [filterTeacher, setFilterTeacher] = useState('all');
  const [filterStudent, setFilterStudent] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Selection states
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Seçili öğrencinin toplam ders ücretini hesapla
  const selectedStudentTotalFee = useMemo(() => {
    if (!filterStudent || filterStudent === 'all' || !Array.isArray(lessons)) return 0;
    
    const studentLessons = lessons.filter(lesson => 
      String(lesson.ogrenci_id) === String(filterStudent)
    );
    
    const total = studentLessons.reduce((sum, lesson) => {
      return sum + (parseFloat(lesson.ders_ucret) || 0) + 
                  (parseFloat(lesson.sirket_payi) || 0) + 
                  (parseFloat(lesson.danisman1) || 0) + 
                  (parseFloat(lesson.danisman2) || 0) + 
                  (parseFloat(lesson.danisman3) || 0);
    }, 0);
    
    return total;
  }, [lessons, filterStudent]);

  const effectiveDateRange = useMemo(() => {
    if (!startDate || !endDate) {
      return {
        start: startDate,
        end: endDate,
        autoCorrected: false
      };
    }

    if (startDate <= endDate) {
      return {
        start: startDate,
        end: endDate,
        autoCorrected: false
      };
    }

    return {
      start: endDate,
      end: startDate,
      autoCorrected: true
    };
  }, [startDate, endDate]);

  const selectedTeacherName = useMemo(() => {
    if (!filterTeacher || filterTeacher === 'all') return '';
    const selectedTeacher = teachers.find(teacher => String(teacher.id) === String(filterTeacher));
    return selectedTeacher?.adi || `#${filterTeacher}`;
  }, [filterTeacher, teachers]);

  const selectedStudentName = useMemo(() => {
    if (!filterStudent || filterStudent === 'all') return '';
    const selectedStudent = students.find(student => String(student.id) === String(filterStudent));
    return selectedStudent?.name || `#${filterStudent}`;
  }, [filterStudent, students]);

  const activeFilterLabels = useMemo(() => {
    const labels = [];
    const trimmedSearch = searchTerm.trim();

    if (trimmedSearch) labels.push(`Arama: "${trimmedSearch}"`);
    if (selectedTeacherName) labels.push(`Öğretmen: ${selectedTeacherName}`);
    if (selectedStudentName) labels.push(`Öğrenci: ${selectedStudentName}`);

    if (effectiveDateRange.start || effectiveDateRange.end) {
      labels.push(`Tarih: ${effectiveDateRange.start || '...'} → ${effectiveDateRange.end || '...'}`);
    }

    return labels;
  }, [
    searchTerm,
    selectedTeacherName,
    selectedStudentName,
    effectiveDateRange.start,
    effectiveDateRange.end
  ]);

  const hasActiveFilters = activeFilterLabels.length > 0;
  
  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoadingFilters(true);
      
      const promises = [
        fetchKursSatis(),
        fetchStudents(),
        fetchTeachers(),
        fetchCourses()
      ];
      
      try {
        await Promise.all(promises);
      } catch (error) {
        console.error('Initial data load error:', error);
      } finally {
        setIsLoadingFilters(false);
      }
    };
    
    loadInitialData();
  }, []);
  
  // Fetch lessons when filters change
  useEffect(() => {
    fetchLessons();
  }, [currentPage, debouncedSearchTerm, filterTeacher, filterStudent, effectiveDateRange.start, effectiveDateRange.end]);
  
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterTeacher, filterStudent, effectiveDateRange.start, effectiveDateRange.end]);
  
  // Fetch lessons with loading state
  const fetchLessons = useCallback(async () => {
    setIsLoadingLessons(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm.trim(),
        ogretmen_id: filterTeacher,
        ogrenci_id: filterStudent,
        start_date: effectiveDateRange.start,
        end_date: effectiveDateRange.end
      };
      
      const response = await axiosConfig.get('/admin/ogretmenders', { params });
      
      if (response.data.pagination) {
        setLessons(response.data.data || []);
        setTotalPages(response.data.pagination.total_pages);
        setTotalRecords(response.data.pagination.total_records);
      } else {
        setLessons(response.data.data || []);
        setTotalPages(1);
        setTotalRecords(response.data.data?.length || 0);
      }
      
      setSelectedIds([]);
    } catch (error) {
      console.error('Dersleri çekerken hata oluştu:', error);
      alert('Dersler yüklenirken bir hata oluştu!');
    } finally {
      setIsLoadingLessons(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearchTerm,
    filterTeacher,
    filterStudent,
    effectiveDateRange.start,
    effectiveDateRange.end
  ]);
  
  // Fetch kurs satis list
  const fetchKursSatis = async () => {
    const extractRows = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      return [];
    };

    const normalizeKursSatis = (item) => ({
      id: item.id,
      ogrenci_id: item.ogrenci_id ?? item.musteri_id ?? '',
      ogrenci_name: item.ogrenci_name || item.musteri_adi || '',
      musteri_adi: item.musteri_adi || item.ogrenci_name || '',
      ogretmenler: item.ogretmenler ?? [],
      dersler: item.dersler ?? [],
      ders_ucret: item.ders_ucret ?? item.ders_ucreti ?? 0,
      ders_ucreti: item.ders_ucreti ?? item.ders_ucret ?? 0
    });

    try {
      let kursSatisData = [];

      try {
        const response = await axiosConfig.get('/admin/ogretmenders/kurssatis/dropdown');
        kursSatisData = extractRows(response.data);
      } catch (primaryError) {
        console.warn('Primary kurs satis dropdown endpoint failed:', primaryError);
      }

      if (!kursSatisData.length) {
        const fallbackResponse = await axiosConfig.get('/admin/kurssatis');
        kursSatisData = extractRows(fallbackResponse.data);
      }

      setKursSatisList(kursSatisData.map(normalizeKursSatis));
    } catch (error) {
      console.error('Kurs satislari cekerken hata olustu:', error);
      setKursSatisList([]);
    }
  };

  // Fetch students
  const fetchStudents = async () => {
    try {
      const response = await axiosConfig.get('/admin/ogrencis');
      
      let studentsData = [];
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        studentsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        studentsData = response.data;
      }
      
      setStudents(studentsData);
    } catch (error) {
      console.error('Öğrencileri çekerken hata oluştu:', error);
      setStudents([]);
    }
  };
  
  // Fetch teachers
  const fetchTeachers = async () => {
    try {
      const response = await axiosConfig.get('/admin/teachers');
      
      let teachersData = [];
      if (Array.isArray(response.data)) {
        teachersData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        teachersData = response.data.data;
      }
      
      setTeachers(teachersData);
    } catch (error) {
      console.error('Öğretmenleri çekerken hata oluştu:', error);
      setTeachers([]);
    }
  };
  
  // Fetch courses
  const fetchCourses = async () => {
    try {
      const response = await axiosConfig.get('/admin/dersgrubu');
      
      let coursesData = [];
      if (Array.isArray(response.data)) {
        coursesData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        coursesData = response.data.data;
      }
      
      setCourses(coursesData);
    } catch (error) {
      console.error('Dersleri çekerken hata oluştu:', error);
      setCourses([]);
    }
  };
  
  // Form operations
  const openFormForCreate = () => {
    setCurrentLesson(null);
    setFormData({
      kurs_satis_id: '',
      ogretmen_id: '',
      ders_id: '',
      ders_konusu: '',
      ders_sure: '40',
      baslangic_tarihi: ''
    });
    setSelectedKursSatis(null);
    setIsFormOpen(true);
  };
  
  const openFormForEdit = (lesson) => {
    setCurrentLesson(lesson);
    
    const formatDateTime = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toISOString().slice(0, 10); // Sadece tarih kısmını al (YYYY-MM-DD)
    };
    
    const kursSatis = kursSatisList.find(ks => ks.id == lesson.kurs_id);
    setSelectedKursSatis(kursSatis || null);
    
    setFormData({
      kurs_satis_id: lesson.kurs_id || '',
      ogretmen_id: lesson.ogretmen_id || '',
      ders_id: lesson.ders_id || '',
      ders_konusu: lesson.ders_konusu || '',
      ders_sure: lesson.ders_sure || '40',
      baslangic_tarihi: formatDateTime(lesson.baslangic_tarihi)
    });
    
    setIsFormOpen(true);
  };
  
  const closeForm = () => {
    setIsFormOpen(false);
    setCurrentLesson(null);
    setSelectedKursSatis(null);
    setFormData({
      kurs_satis_id: '',
      ogretmen_id: '',
      ders_id: '',
      ders_konusu: '',
      ders_sure: '40',
      baslangic_tarihi: ''
    });
  };
  
  // Kurs satışı seçildiğinde
  const parseIdArray = (value) => {
    if (Array.isArray(value)) return value.map(id => String(id));
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map(id => String(id));
      } catch (error) {
        return [];
      }
    }
    return [];
  };

  const getTeacherNamesForKursSatis = (kursSatisItem) => {
    const teacherIds = parseIdArray(kursSatisItem?.ogretmenler);
    const names = teachers
      .filter(teacher => teacherIds.includes(String(teacher.id)))
      .map(teacher => teacher.adi)
      .filter(Boolean);

    return names.length ? names.join(', ') : '-';
  };

  const getCourseNamesForKursSatis = (kursSatisItem) => {
    const courseIds = parseIdArray(kursSatisItem?.dersler);
    const names = courses
      .filter(course => courseIds.includes(String(course.id)))
      .map(course => course.ders_adi)
      .filter(Boolean);

    return names.length ? names.join(', ') : '-';
  };

  const handleKursSatisChange = (e) => {
    const selectedId = e.target.value;
    const selected = kursSatisList.find(ks => ks.id == selectedId);

    setSelectedKursSatis(selected || null);

    if (!selected) {
      setFormData(prev => ({
        ...prev,
        kurs_satis_id: '',
        ogretmen_id: '',
        ders_id: '',
        ders_konusu: ''
      }));
      return;
    }

    const teacherIds = parseIdArray(selected.ogretmenler);
    const courseIds = parseIdArray(selected.dersler);

    const firstTeacherId = teacherIds[0] || '';
    const firstCourseId = courseIds[0] || '';
    const firstCourse = courses.find(course => String(course.id) === String(firstCourseId));

    setFormData(prev => ({
      ...prev,
      kurs_satis_id: selectedId,
      ogretmen_id: firstTeacherId,
      ders_id: firstCourseId,
      ders_konusu: firstCourse?.ders_adi || prev.ders_konusu || ''
    }));
  };

  const getFilteredTeachersForKursSatis = () => {
    if (!selectedKursSatis || !selectedKursSatis.ogretmenler) return [];

    const ogretmenIds = parseIdArray(selectedKursSatis.ogretmenler);
    return teachers.filter(teacher => ogretmenIds.includes(String(teacher.id)));
  };

  const getFilteredCoursesForKursSatis = () => {
    if (!selectedKursSatis || !selectedKursSatis.dersler) return [];

    const dersIds = parseIdArray(selectedKursSatis.dersler);
    return courses.filter(course => dersIds.includes(String(course.id)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.kurs_satis_id || !formData.ogretmen_id || !formData.baslangic_tarihi) {
      alert('Lütfen zorunlu alanları doldurunuz.');
      return;
    }

    const selectedDate = new Date(formData.baslangic_tarihi);
    const today = new Date();
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      alert('Gelecek tarihe ders ekleyemezsiniz.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const dataToSend = {
        kurs_satis_id: formData.kurs_satis_id,
        ogretmen_id: formData.ogretmen_id,
        ders_id: formData.ders_id || null,
        ders_konusu: formData.ders_konusu,
        ders_sure: formData.ders_sure,
        baslangic_tarihi: formData.baslangic_tarihi
      };
      
      if (currentLesson) {
        const updateData = {
          ogrenci_id: currentLesson.ogrenci_id,
          ogretmen_id: formData.ogretmen_id,
          kurs_id: formData.kurs_satis_id,
          ders_id: formData.ders_id || null,
          ders_konusu: formData.ders_konusu,
          ders_sure: parseInt(formData.ders_sure), // Kesinlikle integer olarak gönder
          baslangic_tarihi: formData.baslangic_tarihi
          // Ücretler backend'de ders süresine göre otomatik hesaplanacak
        };
        
        console.log('Güncelleme verisi gönderiliyor:', updateData);
        
        await axiosConfig.put(`/admin/ogretmenders/${currentLesson.id}`, updateData);
        alert('Ders başarıyla güncellendi!');
      } else {
        await axiosConfig.post('/admin/ogretmenders', dataToSend);
        alert('Ders başarıyla eklendi!');
      }
      
      fetchLessons();
      closeForm();
    } catch (error) {
      console.error('Ders kaydedilirken hata oluştu:', error);
      alert(error.response?.data?.message || 'Ders kaydedilirken bir hata oluştu!');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const resolveLessonStatus = (lesson) => {
    const rawStatus = lesson?.ogretmen_status;
    if (rawStatus === null || rawStatus === undefined || rawStatus === '') return 1;
    const parsed = parseInt(rawStatus, 10);
    return Number.isNaN(parsed) ? 1 : parsed;
  };

  const handleToggleLessonStatus = async (lessonId) => {
    setStatusUpdatingId(lessonId);
    try {
      await axiosConfig.patch(`/admin/ogretmenders/${lessonId}/toggle-status`);
      await fetchLessons();
    } catch (error) {
      console.error('Ders durumu değiştirilirken hata oluştu:', error);
      alert('Ders durumu değiştirilirken bir hata oluştu!');
    } finally {
      setStatusUpdatingId(null);
    }
  };
  
  // Delete operations
  const handleDelete = async (id) => {
    if (!window.confirm('Bu dersi silmek istediğinize emin misiniz?')) return;
    
    try {
      await axiosConfig.delete(`/admin/ogretmenders/${id}`);
      alert('Ders başarıyla silindi!');
      fetchLessons();
    } catch (error) {
      console.error('Ders silinirken hata oluştu:', error);
      alert('Ders silinirken bir hata oluştu!');
    }
  };
  
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} dersi silmek istediğinize emin misiniz?`)) return;
    
    setIsSubmitting(true);
    try {
      await axiosConfig.post('/admin/ogretmenders/batch-delete', { ids: selectedIds });
      alert('Seçili dersler başarıyla silindi!');
      fetchLessons();
    } catch (error) {
      console.error('Toplu silme sırasında hata oluştu:', error);
      alert('Toplu silme sırasında bir hata oluştu!');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Selection handlers
  const handleSelectChange = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(sid => sid !== id);
      } else {
        return [...prev, id];
      }
    });
  };
  
  const handleSelectAll = () => {
    if (!Array.isArray(lessons)) return;
    
    if (selectedIds.length === lessons.length) {
      setSelectedIds([]);
    } else {
      const allIds = lessons.map(lesson => lesson.id);
      setSelectedIds(allIds);
    }
  };
  
  // Form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterTeacher('all');
    setFilterStudent('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };
  
  const allSelected = Array.isArray(lessons) && selectedIds.length === lessons.length && lessons.length > 0;
  
  // Loading component
  const LoadingOverlay = () => (
    <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-10">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
        <p className="mt-4 text-lg font-medium text-gray-700">Yükleniyor...</p>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 p-3 sm:p-6">
      <div className="max-w-full mx-auto">
        {/* Modern Header Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 relative">
          {isLoadingLessons && <LoadingOverlay />}
          
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="text-white">
                <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3 mb-2">
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <span>Öğretmen Dersleri Yönetimi</span>
                </h1>
                <p className="text-blue-100 text-lg">
                  Toplam <span className="font-bold text-white">{totalRecords}</span> kayıt bulundu
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <button
                  className="group flex items-center gap-2 px-6 py-3 bg-red-500/20 backdrop-blur text-white font-medium rounded-xl hover:bg-red-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleBatchDelete}
                  disabled={selectedIds.length === 0 || isSubmitting}
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Seçili Sil ({selectedIds.length})
                </button>
                
                <button
                  className="group flex items-center gap-2 px-6 py-3 bg-green-500/20 backdrop-blur text-white font-medium rounded-xl hover:bg-green-500/30 transition-all duration-200"
                  onClick={openFormForCreate}
                >
                  <svg className="w-5 h-5 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Yeni Ders Ekle
                </button>
              </div>
            </div>
          </div>
          
          {/* Info Alert */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-5 mx-6 my-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="font-bold text-blue-900 text-lg mb-1">Yeni Sistem Bilgilendirmesi</h3>
                <p className="text-blue-800">
                  Artık ders eklerken önce <strong className="text-blue-900">Kurs Satışı</strong> seçmeniz gerekmektedir. 
                  Kurs satışı seçildiğinde öğrenci bilgileri ve ücret detayları otomatik olarak gelecektir.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Filters Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filtreler
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Search */}
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <svg className="inline w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Ders Ara
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Konu, öğrenci, öğretmen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={isLoadingFilters}
              />
            </div>
            
            {/* Teacher Filter - Searchable */}
            <SearchableSelect
              options={[{ id: 'all', adi: 'Tümü' }, ...teachers]}
              value={filterTeacher}
              onChange={(e) => setFilterTeacher(e.target.value)}
              placeholder="Öğretmen seçin veya arayın..."
              label="Öğretmen"
              displayKey="adi"
              valueKey="id"
              iconType="teacher"
              disabled={isLoadingFilters}
            />
            
            {/* Student Filter - Searchable */}
            <div className="group">
              <SearchableSelect
                options={[{ id: 'all', name: 'Tümü' }, ...students]}
                value={filterStudent}
                onChange={(e) => setFilterStudent(e.target.value)}
                placeholder="Öğrenci seçin veya arayın..."
                label="Öğrenci"
                displayKey="name"
                valueKey="id"
                iconType="user"
                disabled={isLoadingFilters}
              />
              
              {/* Öğrenci seçildiğinde toplam ücreti göster */}
              {filterStudent && filterStudent !== 'all' && selectedStudentTotalFee > 0 && (
                <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-700">
                      Toplam Ücret:
                    </span>
                    <span className="text-lg font-bold text-blue-900">
                      ₺{selectedStudentTotalFee.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Start Date */}
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <svg className="inline w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Başlangıç Tarihi
              </label>
              <input
                type="date"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 cursor-pointer"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            
            {/* End Date */}
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <svg className="inline w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Bitiş Tarihi
              </label>
              <input
                type="date"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 cursor-pointer"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {hasActiveFilters ? activeFilterLabels.map((label) => (
                <span key={label} className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold">
                  {label}
                </span>
              )) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-50 text-gray-600 border border-gray-200 text-xs font-medium">
                  Aktif filtre yok
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Filtreleri Temizle
            </button>
          </div>

          {effectiveDateRange.autoCorrected && (
            <div className="mt-3 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm">
              Başlangıç tarihi bitişten büyük olduğu için tarih aralığı otomatik düzeltildi.
            </div>
          )}
        </div>
        
        {/* Filtre İstatistik Kartı */}
        {(filterTeacher !== 'all' || filterStudent !== 'all') && (
          <FilterStatisticsCard
            selectedTeacher={filterTeacher}
            selectedStudent={filterStudent}
            lessons={lessons}
            teachers={teachers}
            students={students}
            courses={courses}
          />
        )}
        
        {/* Results Info Bar */}
        <div className="bg-white rounded-xl shadow-md px-6 py-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="text-gray-700">
              Toplam <span className="font-bold text-gray-900">{totalRecords}</span> kayıt
            </span>
            {totalPages > 1 && (
              <span className="text-gray-500">
                • Sayfa <span className="font-semibold">{currentPage}</span> / <span className="font-semibold">{totalPages}</span>
              </span>
            )}
          </div>
        </div>
        
        {/* Modern Table - FULLY RESPONSIVE WITH STICKY */}
        <div className="bg-white rounded-2xl shadow-xl">
          {/* Table for Desktop - FULL RESPONSIVE */}
          <div className="hidden lg:block">
            <div className="overflow-x-auto shadow-inner bg-gray-50 rounded-lg p-1">
              <table className="min-w-[1400px] w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <th className="px-4 py-4 text-center">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                      disabled={!Array.isArray(lessons) || lessons.length === 0}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase">ID</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase min-w-[120px]">Öğrenci</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase min-w-[120px]">Öğretmen</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase">Ders</th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase min-w-[150px]">Konu</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-700 uppercase">Süre</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-700 uppercase">Tarih</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-700 uppercase">Durum</th>
                  <th className="px-2 py-3 text-right text-xs font-bold text-gray-700 uppercase">Ücret</th>
                  <th className="px-2 py-3 text-right text-xs font-bold text-gray-700 uppercase">Şirket</th>
                  <th className="px-2 py-3 text-right text-xs font-bold text-gray-700 uppercase">Dan.1</th>
                  <th className="px-2 py-3 text-right text-xs font-bold text-gray-700 uppercase">Dan.2</th>
                  <th className="px-2 py-3 text-right text-xs font-bold text-gray-700 uppercase">Dan.3</th>
                  <th className="px-2 py-3 text-right text-xs font-bold text-gray-700 uppercase">Toplam</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-700 uppercase sticky right-0 bg-gray-50">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Array.isArray(lessons) && lessons.map((lesson, index) => {
                  const isSelected = selectedIds.includes(lesson.id);
                  const lessonStatus = resolveLessonStatus(lesson);
                  const isLessonPassive = lessonStatus === 0;
                  return (
                    <tr 
                      key={lesson.id} 
                      className={`
                        ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                        ${isSelected ? 'bg-blue-50' : ''}
                        hover:bg-blue-50 transition-colors duration-150
                      `}
                    >
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectChange(lesson.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-2 text-xs font-medium text-gray-900">#{lesson.id}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {(lesson.ogrenci_name || 'Ö')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-gray-900 truncate">{lesson.ogrenci_name || '-'}</div>
                            <div className="text-xs text-gray-500 truncate">{lesson.parent_phone || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-6 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {(lesson.ogretmen_name || 'Ö')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-gray-900 truncate">{lesson.ogretmen_name || '-'}</div>
                            <div className="text-xs text-gray-500 truncate">{lesson.ogretmen_telefon || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          {courses.find(c => c.id == lesson.ders_id)?.ders_adi || '-'}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <div className="max-w-[150px] truncate text-xs text-gray-700" title={lesson.ders_konusu}>
                          {lesson.ders_konusu || '-'}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                          {lesson.ders_sure}dk
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="text-xs text-gray-900">
                          {lesson.baslangic_tarihi
                            ? new Date(lesson.baslangic_tarihi).toLocaleDateString('tr-TR')
                            : '-'}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ${
                            isLessonPassive
                              ? 'bg-red-100 text-red-700 border border-red-200'
                              : 'bg-green-100 text-green-700 border border-green-200'
                          }`}>
                            {isLessonPassive ? 'Pasif' : 'Aktif'}
                          </span>
                          <button
                            type="button"
                            className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${
                              isLessonPassive
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            onClick={() => handleToggleLessonStatus(lesson.id)}
                            disabled={statusUpdatingId === lesson.id}
                            title={isLessonPassive ? 'Dersi aktif yap' : 'Dersi pasif yap'}
                          >
                            {statusUpdatingId === lesson.id
                              ? '...'
                              : isLessonPassive
                                ? 'Aktif Et'
                                : 'Pasif Et'}
                          </button>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-xs font-bold text-green-600">
                          ₺{parseFloat(lesson.ders_ucret || 0).toFixed(0)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-xs font-semibold text-orange-600">
                          ₺{parseFloat(lesson.sirket_payi || 0).toFixed(0)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-xs font-semibold text-blue-600">
                          {lesson.danisman1 > 0 ? `₺${parseFloat(lesson.danisman1).toFixed(0)}` : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-xs font-semibold text-blue-600">
                          {lesson.danisman2 > 0 ? `₺${parseFloat(lesson.danisman2).toFixed(0)}` : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-xs font-semibold text-blue-600">
                          {lesson.danisman3 > 0 ? `₺${parseFloat(lesson.danisman3).toFixed(0)}` : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-xs font-bold text-green-700">
                          ₺{(
                            parseFloat(lesson.ders_ucret || 0) + 
                            parseFloat(lesson.sirket_payi || 0) + 
                            parseFloat(lesson.danisman1 || 0) + 
                            parseFloat(lesson.danisman2 || 0) + 
                            parseFloat(lesson.danisman3 || 0)
                          ).toFixed(0)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center sticky right-0 bg-white border-l border-gray-200">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            className="p-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors duration-200"
                            onClick={() => openFormForEdit(lesson)}
                            title="Düzenle"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            className="p-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors duration-200"
                            onClick={() => handleDelete(lesson.id)}
                            title="Sil"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                
                {(!Array.isArray(lessons) || lessons.length === 0) && (
                  <tr>
                    <td colSpan="16" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center">
                        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-gray-500 text-xl font-medium mb-1">Kayıt bulunamadı</p>
                        <p className="text-gray-400">Filtrelerinizi değiştirerek tekrar arayın</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
            
            {/* Scroll indicator */}
            <div className="mt-2 text-center text-xs text-gray-500">
              <span className="inline-flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Tabloyu yatay kaydırarak tüm kolonları görebilirsiniz
              </span>
            </div>
          </div>
          
          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4 p-4">
            {Array.isArray(lessons) && lessons.map((lesson) => {
              const isSelected = selectedIds.includes(lesson.id);
              return (
                <div key={lesson.id} className={`bg-white rounded-xl shadow-md border ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'} p-5`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectChange(lesson.id)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-xs text-gray-500">ID: #{lesson.id}</span>
                        <h3 className="text-lg font-bold text-gray-900">{lesson.ogrenci_name || 'Öğrenci'}</h3>
                        <p className="text-sm text-gray-600">{lesson.parent_phone || '-'}</p>
                      </div>
                    </div>
                      <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 text-sm font-bold rounded-full">
                        ₺{(
                          parseFloat(lesson.ders_ucret || 0) + 
                          parseFloat(lesson.sirket_payi || 0) + 
                          parseFloat(lesson.danisman1 || 0) + 
                          parseFloat(lesson.danisman2 || 0) + 
                          parseFloat(lesson.danisman3 || 0)
                        ).toFixed(2)}
                      </span>
                    </div>

                    <div className="mb-3 flex items-center justify-between">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        resolveLessonStatus(lesson) === 0
                          ? 'bg-red-100 text-red-700 border border-red-200'
                          : 'bg-green-100 text-green-700 border border-green-200'
                      }`}>
                        {resolveLessonStatus(lesson) === 0 ? 'Pasif Ders' : 'Aktif Ders'}
                      </span>
                      <button
                        type="button"
                        className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                          resolveLessonStatus(lesson) === 0
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-red-600 text-white hover:bg-red-700'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        onClick={() => handleToggleLessonStatus(lesson.id)}
                        disabled={statusUpdatingId === lesson.id}
                      >
                        {statusUpdatingId === lesson.id
                          ? 'İşleniyor...'
                          : resolveLessonStatus(lesson) === 0
                            ? 'Aktif Et'
                            : 'Pasif Et'}
                      </button>
                    </div>
                   
                    <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600 font-medium">Öğretmen:</span>
                      <span className="text-gray-900">{lesson.ogretmen_name || '-'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600 font-medium">Ders:</span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        {courses.find(c => c.id == lesson.ders_id)?.ders_adi || '-'}
                      </span>
                    </div>
                    
                    <div className="text-sm">
                      <span className="text-gray-600 font-medium">Konu:</span>
                      <p className="text-gray-900 mt-1">{lesson.ders_konusu || '-'}</p>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-gray-700">
                          {lesson.baslangic_tarihi
                            ? new Date(lesson.baslangic_tarihi).toLocaleDateString('tr-TR')
                            : '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-700">{lesson.ders_sure} dk</span>
                      </div>
                    </div>
                    
                    {/* Ücret Detayları */}
                    <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100">
                      <div className="text-sm">
                        <span className="text-gray-600">Ders:</span>
                        <span className="ml-2 font-semibold text-green-600">₺{parseFloat(lesson.ders_ucret || 0).toFixed(2)}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Şirket:</span>
                        <span className="ml-2 font-semibold text-orange-600">₺{parseFloat(lesson.sirket_payi || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                    <button
                      className="flex-1 py-2 bg-blue-50 text-blue-600 font-medium rounded-lg hover:bg-blue-100 transition-colors duration-200"
                      onClick={() => openFormForEdit(lesson)}
                    >
                      Düzenle
                    </button>
                    <button
                      className="flex-1 py-2 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100 transition-colors duration-200"
                      onClick={() => handleDelete(lesson.id)}
                    >
                      Sil
                    </button>
                  </div>
                </div>
              );
            })}
            
            {(!Array.isArray(lessons) || lessons.length === 0) && (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-gray-500 text-xl font-medium mb-1">Kayıt bulunamadı</p>
                <p className="text-gray-400">Filtrelerinizi değiştirerek tekrar arayın</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 bg-white rounded-xl shadow-lg p-5">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Sayfa <span className="font-bold text-gray-900">{currentPage}</span> / <span className="font-bold text-gray-900">{totalPages}</span>
                <span className="ml-2 text-gray-500">({totalRecords} kayıt)</span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Önceki
                </button>
                
                {/* Page numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 2 && page <= currentPage + 2);
                    })
                    .map((page, index, array) => {
                      if (index > 0 && array[index - 1] < page - 1) {
                        return (
                          <React.Fragment key={`ellipsis-${page}`}>
                            <span className="px-2 text-gray-400">...</span>
                            <button
                              className={`px-3 py-1 rounded-lg transition-colors duration-200 ${
                                currentPage === page
                                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      }
                      return (
                        <button
                          key={page}
                          className={`px-3 py-1 rounded-lg transition-colors duration-200 ${
                            currentPage === page
                              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    })}
                </div>
                
                <button
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Form Modal - Modern Design */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto relative">
            {isSubmitting && <LoadingOverlay />}

            <h3 className="text-xl font-bold mb-4">
              {currentLesson ? 'Dersi Duzenle' : 'Yeni Ders Ekle'}
            </h3>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <SearchableSelect
                    options={kursSatisList.map(ks => ({
                      ...ks,
                      id: String(ks.id),
                      student_text: ks.ogrenci_name || ks.musteri_adi || ('#' + (ks.ogrenci_id || ks.id)),
                      teacher_text: getTeacherNamesForKursSatis(ks),
                      course_text: getCourseNamesForKursSatis(ks),
                      price_text: Number(ks.ders_ucret || ks.ders_ucreti || 0).toFixed(2),
                      display_text: `#${ks.id} - ${(ks.ogrenci_name || ks.musteri_adi || ('#' + (ks.ogrenci_id || ks.id)))} - ${getCourseNamesForKursSatis(ks)}`
                    }))}
                    value={String(formData.kurs_satis_id || '')}
                    onChange={handleKursSatisChange}
                    name="kurs_satis_id"
                    placeholder="Kurs Satis secin veya arayin..."
                    label="Kurs Satis Secin"
                    displayKey="display_text"
                    valueKey="id"
                    showIcon={false}
                    renderOption={(option) => (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-gray-900 truncate">
                            #{option.id} {option.student_text}
                          </span>
                          <span className="shrink-0 px-2 py-0.5 rounded-md bg-green-50 text-green-700 text-xs font-semibold">
                            TL {option.price_text}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div className="truncate">
                            <span className="font-medium text-gray-700">Ogretmen: </span>
                            {option.teacher_text}
                          </div>
                          <div className="truncate">
                            <span className="font-medium text-gray-700">Kurs: </span>
                            {option.course_text}
                          </div>
                        </div>
                      </div>
                    )}
                    disabled={isSubmitting || currentLesson}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Kurs satisi secimi zorunludur. Secince ogretmen ve ders otomatik doldurulur.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ogrenci
                  </label>
                  <input
                    type="text"
                    value={selectedKursSatis ? (selectedKursSatis.ogrenci_name || selectedKursSatis.musteri_adi || '-') : ''}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600"
                    disabled
                    readOnly
                  />
                </div>

                <div>
                  <SearchableSelect
                    options={(currentLesson ? teachers : getFilteredTeachersForKursSatis()).map(teacher => ({
                      ...teacher,
                      id: String(teacher.id)
                    }))}
                    value={String(formData.ogretmen_id || '')}
                    onChange={handleInputChange}
                    name="ogretmen_id"
                    placeholder="Ogretmen secin veya arayin..."
                    label="Ogretmen"
                    displayKey="adi"
                    valueKey="id"
                    showIcon={false}
                    disabled={isSubmitting || !formData.kurs_satis_id}
                    required
                  />
                </div>

                <div>
                  <SearchableSelect
                    options={(currentLesson ? courses : getFilteredCoursesForKursSatis()).map(course => ({
                      ...course,
                      id: String(course.id)
                    }))}
                    value={String(formData.ders_id || '')}
                    onChange={handleInputChange}
                    name="ders_id"
                    placeholder="Ders secin veya arayin..."
                    label="Ders"
                    displayKey="ders_adi"
                    valueKey="id"
                    showIcon={false}
                    disabled={isSubmitting || !formData.kurs_satis_id}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ders Konusu
                  </label>
                  <input
                    type="text"
                    name="ders_konusu"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                    value={formData.ders_konusu}
                    onChange={handleInputChange}
                    disabled={isSubmitting || !formData.kurs_satis_id}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Baslangic Tarihi
                  </label>
                  <input
                    type="date"
                    name="baslangic_tarihi"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                    value={formData.baslangic_tarihi}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split('T')[0]}
                    disabled={isSubmitting || !formData.kurs_satis_id}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    * Gecmis tarihler dahil bugune kadar tarih secebilirsiniz
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ders Suresi
                  </label>
                  <select
                    name="ders_sure"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                    value={formData.ders_sure}
                    onChange={handleInputChange}
                    disabled={isSubmitting || !formData.kurs_satis_id}
                    required
                  >
                    <option value="40">40 Dakika</option>
                    <option value="60">60 Dakika</option>
                    <option value="80">80 Dakika</option>
                    <option value="120">120 Dakika</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Iptal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.kurs_satis_id || !formData.ogretmen_id || !formData.ders_id}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Isleniyor...' : (currentLesson ? 'Guncelle' : 'Ekle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
